import type { MarketplaceOrder } from "../types/marketplace.types";

/** Stored delivery dates are calendar `YYYY-MM-DD` strings with no timezone. */
const YMD_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const MS_PER_DAY = 86_400_000;

/** Display-only. Age is not a deadline, so it stays out of `DeadlineRollup`. */
export function orderAgeDays(
  createdAtIso: string,
  now: Date = new Date(),
): number {
  const createdMs = Date.parse(createdAtIso);
  if (!Number.isFinite(createdMs)) return 0;
  const elapsed = now.getTime() - createdMs;
  if (elapsed < MS_PER_DAY) return 0;
  return Math.floor(elapsed / MS_PER_DAY);
}

/** Deadlines this many days out or closer read as "soon" rather than "scheduled". */
export const DEADLINE_SOON_THRESHOLD_DAYS = 7;

export type DeadlineTone = "overdue" | "today" | "soon" | "scheduled";

export interface DeadlineInfo {
  ymd: string;
  /** Negative when the date is in the past. */
  daysFromToday: number;
  tone: DeadlineTone;
}

/**
 * Parse a `YYYY-MM-DD` string into a local-midnight Date. Built from calendar
 * parts so `new Date("YYYY-MM-DD")` cannot shift the day at UTC midnight.
 */
export function parseYmdToLocalDate(ymd: string): Date | null {
  const match = YMD_PATTERN.exec(ymd);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

export function toLocalYmd(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function localTodayYmd(now: Date = new Date()): string {
  return toLocalYmd(now);
}

/** Whole calendar days from `fromYmd` to `toYmd` (positive when `toYmd` is later). */
export function daysBetweenYmd(fromYmd: string, toYmd: string): number | null {
  const from = parseYmdToLocalDate(fromYmd);
  const to = parseYmdToLocalDate(toYmd);
  if (!from || !to) return null;
  // Round to absorb DST hour shifts between the two local midnights.
  return Math.round((to.getTime() - from.getTime()) / MS_PER_DAY);
}

export function describeDeadline(
  ymd: string | null | undefined,
  todayYmd: string,
): DeadlineInfo | null {
  if (!ymd) return null;
  const daysFromToday = daysBetweenYmd(todayYmd, ymd);
  if (daysFromToday === null) return null;

  let tone: DeadlineTone;
  if (daysFromToday < 0) tone = "overdue";
  else if (daysFromToday === 0) tone = "today";
  else if (daysFromToday <= DEADLINE_SOON_THRESHOLD_DAYS) tone = "soon";
  else tone = "scheduled";

  return { ymd, daysFromToday, tone };
}

/** Quick Extend presets, in calendar days. */
export const EXTEND_DUE_PRESET_DAYS = [3, 7] as const;

export function addDaysToYmd(ymd: string, days: number): string | null {
  const date = parseYmdToLocalDate(ymd);
  if (!date) return null;
  date.setDate(date.getDate() + days);
  return toLocalYmd(date);
}

/**
 * Extend counts from today when the date has already passed, so "+3 days" on
 * a long-overdue item lands in the future instead of staying overdue.
 */
export function extendDueYmd(
  dueYmd: string,
  todayYmd: string,
  days: number,
): string | null {
  const base = dueYmd < todayYmd ? todayYmd : dueYmd;
  return addDaysToYmd(base, days);
}

/** Locale-shaped absolute date, e.g. "23 Aug 2026" — the hover companion to the relative label. */
export function formatYmdLong(ymd: string, locale?: string): string {
  const date = parseYmdToLocalDate(ymd);
  if (!date) return ymd;
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

/** Shorter absolute date for dense rows, e.g. "23 Aug". Adds the year only when it differs from today's. */
export function formatYmdShort(
  ymd: string,
  todayYmd: string,
  locale?: string,
): string {
  const date = parseYmdToLocalDate(ymd);
  if (!date) return ymd;
  const sameYear = ymd.slice(0, 4) === todayYmd.slice(0, 4);
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  }).format(date);
}

/**
 * The single deadline an order row surfaces: the earliest script SLA date,
 * then the order ETA when no script has its own deadline.
 */
export function orderDeadlineYmd(
  order: Pick<MarketplaceOrder, "etaDate"> & {
    scripts: ReadonlyArray<{ expectedDeliveryDate?: string | null }>;
  },
): string | null {
  const dates = order.scripts
    .map((script) => script.expectedDeliveryDate)
    .filter(
      (date): date is string =>
        typeof date === "string" && YMD_PATTERN.test(date),
    );
  if (dates.length > 0) return [...dates].sort()[0] ?? null;
  return order.etaDate;
}
